import type {
  BehaviorConnection,
  BehaviorNode,
} from "../../../../util/src/blueprint/types.js";
import type { LayoutNode, SceneBehaviorCatalog } from "../layout-types";
import { EMPTY_SCENE_CATALOG } from "../../../../util/src/layout/types.js";
import { sceneCatalogHasEntries } from "../../../../util/src/layout/scene-catalog-clipboard.js";

export type ClipboardPayloadVersion = 1;
export type ClipboardEntityType = "layout" | "behavior";

type ClipboardPayloadBase = {
  kind: "meet-manager/editor-clipboard";
  version: ClipboardPayloadVersion;
  entity: ClipboardEntityType;
  source?: {
    layoutId?: string;
    tabType?: "display" | "behavior" | "sceneBehavior";
    copiedAt?: string;
  };
};

export type LayoutClipboardPayload = ClipboardPayloadBase & {
  entity: "layout";
  data: {
    nodes: LayoutNode[];
    /**
     * Closed set of scene catalog entries referenced by `nodes`
     * (animations / events / functions / variables), for cross-layout paste.
     */
    catalog?: SceneBehaviorCatalog;
  };
};

function parseOptionalSceneCatalog(
  value: unknown,
): SceneBehaviorCatalog | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Partial<SceneBehaviorCatalog>;
  const catalog: SceneBehaviorCatalog = {
    animations: Array.isArray(raw.animations) ? raw.animations : [],
    customEvents: Array.isArray(raw.customEvents) ? raw.customEvents : [],
    functions: Array.isArray(raw.functions) ? raw.functions : [],
    variables: Array.isArray(raw.variables) ? raw.variables : [],
    ...(Array.isArray(raw.objectTypes) ? { objectTypes: raw.objectTypes } : {}),
  };
  return sceneCatalogHasEntries(catalog) ? catalog : { ...EMPTY_SCENE_CATALOG };
}

export type BehaviorClipboardPayload = ClipboardPayloadBase & {
  entity: "behavior";
  data: {
    nodes: BehaviorNode[];
    connections: BehaviorConnection[];
  };
};

export type EditorClipboardPayload =
  | LayoutClipboardPayload
  | BehaviorClipboardPayload;

/** Shared across browser tabs on the same origin (layout editor opened in _blank). */
export const CROSS_TAB_CLIPBOARD_STORAGE_KEY = "meet-manager/editor-clipboard";

let inMemoryClipboardPayload: EditorClipboardPayload | null = null;

function readClipboardPayloadFromCrossTabStorage(): EditorClipboardPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const text = localStorage.getItem(CROSS_TAB_CLIPBOARD_STORAGE_KEY);
    return text ? parseClipboardPayload(text) : null;
  } catch {
    return null;
  }
}

function persistClipboardPayloadToCrossTabStorage(
  payload: EditorClipboardPayload,
): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      CROSS_TAB_CLIPBOARD_STORAGE_KEY,
      serializeClipboardPayload(payload),
    );
  } catch {
    // Ignore quota or private-mode failures; system clipboard may still work.
  }
}

function clearCrossTabClipboardStorage(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(CROSS_TAB_CLIPBOARD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function setInMemoryClipboardPayload(
  payload: EditorClipboardPayload | null,
): void {
  inMemoryClipboardPayload = payload;
  if (payload) {
    persistClipboardPayloadToCrossTabStorage(payload);
  } else {
    clearCrossTabClipboardStorage();
  }
}

export function getInMemoryClipboardPayload(): EditorClipboardPayload | null {
  return inMemoryClipboardPayload ?? readClipboardPayloadFromCrossTabStorage();
}

/**
 * Reads the editor clipboard from every supported source (in-memory, cross-tab
 * storage, then system clipboard).
 */
export async function readClipboardPayloadFromAllSources(): Promise<EditorClipboardPayload | null> {
  const local = getInMemoryClipboardPayload();
  if (local) return local;
  const fromSystem = await readClipboardPayloadFromSystem();
  if (fromSystem) {
    inMemoryClipboardPayload = fromSystem;
    persistClipboardPayloadToCrossTabStorage(fromSystem);
  }
  return fromSystem;
}

export function serializeClipboardPayload(
  payload: EditorClipboardPayload,
): string {
  return JSON.stringify(payload);
}

export function parseClipboardPayload(
  text: string,
): EditorClipboardPayload | null {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text) as {
      kind?: string;
      version?: number;
      entity?: string;
      source?: EditorClipboardPayload["source"];
      data?: {
        nodes?: unknown;
        connections?: unknown;
        catalog?: unknown;
      };
    };
    if (
      parsed.kind !== "meet-manager/editor-clipboard" ||
      parsed.version !== 1 ||
      (parsed.entity !== "layout" && parsed.entity !== "behavior")
    ) {
      return null;
    }
    if (parsed.entity === "layout") {
      const nodes = parsed.data?.nodes;
      if (!Array.isArray(nodes)) return null;
      const catalog = parseOptionalSceneCatalog(parsed.data?.catalog);
      return {
        kind: "meet-manager/editor-clipboard",
        version: 1,
        entity: "layout",
        source: parsed.source,
        data: {
          nodes: nodes as LayoutNode[],
          ...(catalog && sceneCatalogHasEntries(catalog) ? { catalog } : {}),
        },
      };
    }
    const nodes = parsed.data?.nodes;
    const connections = parsed.data?.connections;
    if (!Array.isArray(nodes) || !Array.isArray(connections)) return null;
    return {
      kind: "meet-manager/editor-clipboard",
      version: 1,
      entity: "behavior",
      source: parsed.source,
      data: {
        nodes: nodes as BehaviorNode[],
        connections: connections as BehaviorConnection[],
      },
    };
  } catch {
    return null;
  }
}

export function isClipboardPayloadForScope(
  payload: EditorClipboardPayload | null,
  scope: ClipboardEntityType,
): boolean {
  return payload?.entity === scope;
}

export async function writeClipboardPayloadToSystem(
  payload: EditorClipboardPayload,
): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(serializeClipboardPayload(payload));
    return true;
  } catch {
    return false;
  }
}

export async function readClipboardPayloadFromSystem(): Promise<EditorClipboardPayload | null> {
  if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
    return null;
  }
  try {
    const text = await navigator.clipboard.readText();
    return parseClipboardPayload(text);
  } catch {
    return null;
  }
}
